import { useEffect, useMemo, useRef, useState } from 'react';
import { Experience, ExperienceVariant } from '@/webapi/use-experience-api';

const isActiveVariant = (v: ExperienceVariant) => (v.chance ?? 0) > 0;

const computeChancesSum = (variants: ExperienceVariant[]) =>
  variants.reduce((sum, v) => sum + (v.chance ?? 0), 0);

export function useBaselineVariant(
  currentExperience: Experience,
  variants: ExperienceVariant[],
  setEditorBaselineVariantId: (id: string | null) => void,
) {
  const [isBaselineEditMode, setIsBaselineEditMode] = useState(false);
  const [baselineVariantId, setBaselineVariantId] = useState<string | null>(
    currentExperience.baselineVariantId ?? null,
  );
  const savedBaselineRef = useRef<string | null>(
    currentExperience.baselineVariantId ?? null,
  );

  const showUpdateBaseline = useMemo(() => {
    const activeVariants = variants?.filter(isActiveVariant) ?? [];
    return (
      activeVariants.length >= 2 && computeChancesSum(activeVariants) === 100
    );
  }, [variants]);

  const revertToSavedBaseline = () => {
    setBaselineVariantId(savedBaselineRef.current);
    setEditorBaselineVariantId(savedBaselineRef.current);
  };

  const handleSelectBaseline = (id: string | null) => {
    setBaselineVariantId(id);
    setEditorBaselineVariantId(id);
  };

  const onToggleBaseline = () => {
    if (!isBaselineEditMode) {
      savedBaselineRef.current = currentExperience.baselineVariantId ?? null;
    } else {
      revertToSavedBaseline();
    }
    setIsBaselineEditMode((prev) => !prev);
  };

  useEffect(() => {
    if (!showUpdateBaseline && isBaselineEditMode) {
      setIsBaselineEditMode(false);
      revertToSavedBaseline();
    }
  }, [showUpdateBaseline]);

  return {
    isBaselineEditMode,
    baselineVariantId,
    showUpdateBaseline,
    handleSelectBaseline,
    onToggleBaseline,
  };
}
